<template>
  <v-dialog
    v-model="dialogModel"
    scrollable
    max-width="800"
  >
    <v-card v-if="message">
      <v-card-title class="headline grey lighten-2 txn-details-title">
        <span class="txn-details-title__text">{{ message.recipientLabel }} Message details</span>
        <v-btn
          icon
          class="txn-details-title__close"
          aria-label="Close"
          @click="closeDialog"
        >
          <v-icon>fas fa-times</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div class="txn-details txn-split pt-3">
          <div class="txn-split__cards">
          <div class="info-card">
            <div class="info-card__head">
              <v-icon
                v-if="message.typeIcon"
                class="info-card__icon"
              >{{ message.typeIcon }}</v-icon>
              <span class="info-card__label">{{ message.typeLabel }}</span>
            </div>
            <div class="info-card__value">{{ message.recipientLabel }}</div>
            <v-layout row wrap>
              <v-flex sm4 pr-1 class="text-xs-right">
                <strong>Status</strong>
              </v-flex>
              <v-flex sm8 pl-1 class="layout row align-center">
                <v-icon
                  v-if="message.statusIconData"
                  small
                  class="mr-1"
                  :color="message.statusIconData.color"
                >{{ message.statusIconData.icon }}</v-icon>
                <div>{{ message.statusLabel }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex sm4 pr-1 class="text-xs-right">
                <strong>Retries</strong>
              </v-flex>
              <v-flex sm8 pl-1>
                <div>{{ message.retryLabel }}</div>
              </v-flex>
            </v-layout>
          </div>

          <div class="info-card">
            <div class="info-card__head">
              <v-icon class="info-card__icon">far fa-align-left</v-icon>
              <span class="info-card__label">Content</span>
            </div>
            <div class="info-card__text">{{ message.contentLabel }}</div>
          </div>

          <div class="info-card">
            <div class="info-card__head">
              <v-icon class="info-card__icon">far fa-clock</v-icon>
              <span class="info-card__label">Dates</span>
            </div>
            <v-layout row wrap>
              <v-flex sm4 pr-1 class="text-xs-right">
                <strong>Created</strong>
              </v-flex>
              <v-flex sm8 pl-1>
                <div>{{ message.dateCreatedLabel }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex sm4 pr-1 class="text-xs-right">
                <strong>Updated</strong>
              </v-flex>
              <v-flex sm8 pl-1>
                <div>{{ message.dateUpdatedLabel }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap v-if="message.idLabel">
              <v-flex sm4 pr-1 class="text-xs-right">
                <strong>ID</strong>
              </v-flex>
              <v-flex sm8 pl-1>
                <div>{{ message.idLabel }}</div>
              </v-flex>
            </v-layout>
          </div>

          <div class="info-card">
            <div class="info-card__head">
              <v-icon class="info-card__icon">far fa-paper-plane</v-icon>
              <span class="info-card__label">Provider delivery</span>
            </div>
            <v-layout row wrap>
              <v-flex sm4 pr-1 class="text-xs-right">
                <strong>Delivery</strong>
              </v-flex>
              <v-flex sm8 pl-1 class="layout row align-center">
                <v-icon
                  v-if="message.deliveryStatusIconData"
                  small
                  class="mr-1"
                  :color="message.deliveryStatusIconData.color"
                >{{ message.deliveryStatusIconData.icon }}</v-icon>
                <div>{{ message.deliveryStatusLabel }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex sm4 pr-1 class="text-xs-right">
                <strong>Provider status</strong>
              </v-flex>
              <v-flex sm8 pl-1>
                <div>{{ message.providerStatusLabel }}</div>
              </v-flex>
            </v-layout>
            <v-layout
              row
              wrap
              v-for="(report, index) in deliveryReports"
              :key="'delivery-' + index"
            >
              <v-flex sm4 pr-1 class="text-xs-right">
                <strong>{{ report.toLabel }}</strong>
              </v-flex>
              <v-flex sm8 pl-1>
                <div>{{ report.statusLabel }}</div>
              </v-flex>
            </v-layout>
          </div>

          <div v-if="errorEntries.length" class="info-card">
            <div class="info-card__head">
              <v-icon class="info-card__icon">far fa-exclamation-triangle</v-icon>
              <span class="info-card__label">Errors</span>
            </div>
            <div
              v-for="(entry, index) in errorEntries"
              :key="'error-' + index"
              class="info-card__text txn-error"
            >{{ entry.message || entry }}</div>
          </div>
          </div>

          <div class="txn-split__aside">
            <div class="info-card">
              <div class="info-card__head">
                <v-icon class="info-card__icon">far fa-code</v-icon>
                <span class="info-card__label">Metadata</span>
              </div>
              <div v-if="hasMetadata" class="txn-json-viewer-wrap">
                <json-viewer
                  :value="metadataValue"
                  :expand-depth="1"
                  boxed
                  copyable
                  sort
                  class="txn-json-viewer"
                />
              </div>
              <div v-else class="info-card__placeholder">No metadata available</div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.txn-details {
  border-top: 1px solid #ccc;
}

.txn-details .info-card {
  height: auto;
  margin-bottom: 8px;
}

.txn-split {
  display: flex;
  align-items: stretch;
}

.txn-split__cards {
  width: 50%;
  flex: 0 0 50%;
  min-width: 0;
  padding-right: 8px;
}

.txn-split__aside {
  width: 50%;
  flex: 0 0 50%;
  min-width: 0;
}

.txn-split__aside > .info-card {
  margin-bottom: 0;
  height: 100%;
}

.txn-details .info-card__text {
  white-space: pre-wrap;
  word-break: break-word;
}

.txn-details ::v-deep .flex.sm8 {
  min-width: 0;
  word-break: break-word;
}

.txn-json-viewer-wrap {
  margin-top: 4px;
}

.txn-json-viewer {
  font-size: 12px;
}

.txn-details ::v-deep .txn-json-viewer .jv-code {
  padding: 12px;
}

.txn-details-title {
  display: flex;
  align-items: center;
  padding-top: 12px;
  padding-bottom: 12px;
}

.txn-details-title__text {
  flex: 1;
  min-width: 0;
  text-align: center;
}

.txn-details-title__close {
  flex: 0 0 auto;
  margin: 0;
}

.txn-error {
  max-height: 7.5rem;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  color: #c43e18;
}
</style>

<script>
import src from './src'
export default src
</script>
